import { formatNumber } from "@/utilities/NumberUtils";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface QuoteTotalsProps {
  amount: number;
  transferFee: number;
  paymentMethodFee: number;
  total: number;
}

export const QuoteTotals = ({
  amount,
  transferFee,
  paymentMethodFee,
  total,
}: QuoteTotalsProps) => {
  const [t] = useTranslation("global");
  const [open, setOpen] = useState(false);

  return (
    <div
      className={classNames(
        "w-full flex flex-col gap-[6px] h-fit bg-[#E9EAED] rounded-xl p-3 allow-keywords transition-all"
      )}
    >
      <div className="w-full flex justify-between text-gray-600 text-sm font-medium gap-x-2 pr-6">
        <p className="text-inherit select-none">{t("Quoter.Total.Send")}</p>
        <p className="text-inherit select-none text-right">
          {formatNumber(amount)} USD
        </p>
      </div>
      <div
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between text-gray-600 text-sm font-medium gap-x-2 cursor-pointer"
      >
        <p className="text-inherit select-none">{t("Quoter.Total.Fee")}</p>
        <div className="flex items-center gap-1">
          <p className="text-inherit select-none text-right">
            {formatNumber(transferFee + paymentMethodFee)} USD
          </p>
          <ChevronDownIcon
            className={classNames(
              "w-5 h-4 text-[#828892] transition-all stroke-[3px] cursor-pointer",
              {
                "rotate-180": open,
              }
            )}
          />
        </div>
      </div>

      <div className="w-full flex flex-col">
        <div
          className={classNames(
            "w-full flex flex-col transition-all overflow-hidden",
            {
              "h-auto px-6 pb-2": open,
              "h-0": !open,
            }
          )}
        >
          <div className="w-full flex justify-between text-[#828892] text-[13px] sm:text-sm leading-tight font-medium gap-x-2">
            <p className="text-inherit select-none">
              {t("Quoter.Total.TransactionFee")}
            </p>
            <p className="text-inherit select-none text-right">
              {formatNumber(transferFee)} USD
            </p>
          </div>
          <div className="w-full flex justify-between text-[#828892] text-[13px] sm:text-sm leading-tight font-medium gap-x-2 mt-1">
            <p className="text-inherit select-none">
              {t("Quoter.Total.PaymentMethodFee")}
            </p>
            <p className="text-inherit select-none text-right">
              {formatNumber(paymentMethodFee)} USD
            </p>
          </div>
        </div>
        <div className="w-full h-[1px] flex-shrink-0 bg-[#828892]" />
      </div>

      <div className="w-full flex justify-between text-gray-900 font-semibold">
        <p className="text-inherit select-none">{t("Quoter.Total.Total")}</p>
        <p className="text-inherit select-none text-right">
          {formatNumber(total)} USD
        </p>
      </div>
    </div>
  );
};
